/**
 * API client for communicating with the backend.
 * Handles auth token injection and error normalization.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

interface ApiError {
  type: string;
  message: string;
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly type: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Get stored auth token (will be set by auth service later) */
let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

/** Set the ID token used for Cognito authorizer validation */
let idToken: string | null = null;

export function setIdToken(token: string | null): void {
  idToken = token;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 304) {
    // Not Modified — caller handles
    return undefined as unknown as T;
  }

  if (!response.ok) {
    let errorBody: { error?: ApiError } = {};
    try {
      errorBody = await response.json();
    } catch {
      // ignore parse errors
    }

    throw new ApiRequestError(
      response.status,
      errorBody.error?.type ?? 'UNKNOWN_ERROR',
      errorBody.error?.message ?? `HTTP ${response.status}`
    );
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),

  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
